/**
 * CaregiverCheckIn — "How are you doing today?" Sits ABOVE all loved-one
 * content on both home screens.
 *
 * Tap → backend returns Tier 1 (founder-written, verbatim, no history) or
 * Tier 2 (generated from HER record — Lane remembers) content: response,
 * follow-up question, and quick-reply chips. Free text always available.
 * Chip or typed words become the first message of a Lane conversation.
 *
 * Tier 3 (crisis): if free text signals risk, a FIXED safety panel renders —
 * never generated, never varied. Resources shown immediately, not behind a tap.
 */
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import {
    HeartHandshake, MessageCircle, Smile, Wind, CloudRain, Compass, BatteryLow,
    Loader2, Send, Phone, MessageSquare,
} from "lucide-react";
import { track, EVENTS } from "@/lib/analytics";
import { stripPlaceholder } from "@/lib/safeContent";

export const CAREGIVER_OPTIONS = [
    { code: "holding_up_okay", label: "I'm holding up okay", day: "an okay", icon: Smile, color: "#5B8467", soft: "#EFF5F0" },
    { code: "stretched_thin", label: "Stretched thin", day: "a stretched-thin", icon: Wind, color: "#A98444", soft: "#F8F4EA" },
    { code: "overwhelmed", label: "Overwhelmed", day: "an overwhelming", icon: CloudRain, color: "#B4703F", soft: "#F9F0E8" },
    { code: "scared_whats_coming", label: "Scared about what's coming", day: "a scared", icon: Compass, color: "#75719E", soft: "#F2F1F7" },
    { code: "running_on_empty", label: "Running on empty", day: "a running-on-empty", icon: BatteryLow, color: "#B15D52", soft: "#F9EDEB" },
];

const HARD_CODES = new Set(["stretched_thin", "overwhelmed", "scared_whats_coming", "running_on_empty"]);

// Acknowledge and invite — never label her day for her.
function relativeHeading(last) {
    if (!last?.code || !last?.created_at) return null;
    const then = new Date(last.created_at);
    if (Number.isNaN(then.getTime())) return null;
    const days = Math.floor((Date.now() - then.getTime()) / (24 * 60 * 60 * 1000));
    if (then.toDateString() === new Date().toDateString()) return "You checked in earlier. How are you feeling now?";
    if (days <= 1) {
        return HARD_CODES.has(last.code)
            ? "Yesterday sounded like a hard day. How are you feeling today?"
            : "Let's start with you today. How are you feeling right now?";
    }
    if (days <= 7) return "Let's start with you today. How are you feeling right now?";
    return null;
}

const OPEN_ENDED = (chip) => /(\.\.\.|…)\s*$/.test(chip);

export default function CaregiverCheckIn({ firstVisit, onResponded }) {
    const navigate = useNavigate();
    const [last, setLast] = useState(null);
    const [lastLoaded, setLastLoaded] = useState(false);
    const [picked, setPicked] = useState(null);
    const [loading, setLoading] = useState(false);
    const [content, setContent] = useState(null); // {checkin_id, tier, text, follow_up, quick_replies}
    const [freeText, setFreeText] = useState("");
    const [sending, setSending] = useState(false);
    const [crisis, setCrisis] = useState(null); // {path, flag_id}
    const inputRef = useRef(null);

    useEffect(() => {
        // Never flash the generic heading before we know whether she's a
        // returning caregiver — neutral skeleton until this resolves.
        api.get("/me/caregiver-checkin/last")
            .then((r) => setLast(r.data || null))
            .catch(() => {})
            .finally(() => setLastLoaded(true));
    }, []);

    const tap = async (code) => {
        if (loading || picked) return;
        setLoading(true);
        setPicked(code);
        try {
            track(firstVisit ? EVENTS.CAREGIVER_CHECKIN_FIRST_VISIT : EVENTS.CAREGIVER_CHECKIN_RETURNING, { code });
        } catch (_e) { /* best-effort */ }
        try {
            const r = await api.post("/me/caregiver-checkin", { code });
            const d = r.data || {};
            setContent({
                ...d,
                text: stripPlaceholder(d.text) || "Lane hears you. However today feels, it counts — and checking in on yourself is part of caring well.",
                follow_up: stripPlaceholder(d.follow_up) || "What would make today feel a little lighter?",
            });
        } catch (_e) {
            setContent({ text: "Lane hears you. However today feels, it counts — and checking in on yourself is part of caring well.", follow_up: "What would make today feel a little lighter?", quick_replies: [] });
        }
        setLoading(false);
        onResponded?.(code);
    };

    const submitAnswer = async (answerText, answerType) => {
        if (sending || !answerText.trim()) return;
        setSending(true);
        let risk = null, flagId = null;
        try {
            if (content?.checkin_id) {
                const r = await api.post("/me/caregiver-checkin/answer", {
                    checkin_id: content.checkin_id, answer_text: answerText, answer_type: answerType,
                });
                risk = r.data?.risk || null;
                flagId = r.data?.flag_id || null;
            }
        } catch (_e) {
            // Defense-in-depth: if the screening endpoint failed, run a local
            // keyword check so crisis language never silently routes to chat.
            if (/(kill myself|suicid|want to die|end my life|hurt myself|not worth living|can't go on|hurt (him|her|them))/i.test(answerText)) {
                risk = "caregiver";
            }
        }
        setSending(false);
        if (risk) {
            setCrisis({ path: risk, flag_id: flagId });
            return; // fixed safety pathway — no chat handoff, no generation
        }
        const label = CAREGIVER_OPTIONS.find((o) => o.code === picked)?.label || "checking in";
        // Mid-sentence casing: keep "I'm…" capitalized, lowercase the rest.
        const mid = /^i(['\s]|$)/i.test(label) ? label : label.charAt(0).toLowerCase() + label.slice(1);
        navigate(`/chat?prompt=${encodeURIComponent(`I just checked in — ${mid}. ${answerText}`.trim())}`);
    };

    const chipTap = (chip) => {
        if (OPEN_ENDED(chip)) {
            // Seed the composer with the chip text (minus the ellipsis) BEFORE
            // focusing, so she continues the sentence instead of starting cold.
            setFreeText(chip.replace(/(\.\.\.|…)\s*$/, "").trimEnd() + " ");
            requestAnimationFrame(() => inputRef.current?.focus());
            return;
        }
        submitAnswer(chip, "chip");
    };

    const heading = relativeHeading(last) || "How are you doing today?";
    const pickedOpt = picked ? CAREGIVER_OPTIONS.find((o) => o.code === picked) : null;

    return (
        <section
            data-testid="caregiver-checkin"
            className="mt-8 rounded-2xl border border-[#E4E8EC] bg-white p-5 md:p-7 shadow-[0_1px_2px_rgba(16,42,67,0.04)]"
        >
            <div className="flex items-start gap-3">
                <span className="w-10 h-10 rounded-full bg-[#EDF4F5] flex items-center justify-center shrink-0">
                    <HeartHandshake size={17} strokeWidth={1.5} className="text-[#12776F]" />
                </span>
                {crisis ? (
                    <>
                        <CrisisInlineNote path={crisis.path} />
                        {!crisis.dismissed && (
                            <CrisisPanel
                                path={crisis.path}
                                flagId={crisis.flag_id}
                                onClose={() => setCrisis((c) => ({ ...c, dismissed: true }))}
                            />
                        )}
                    </>
                ) : loading ? (
                    <div className="flex-1 flex items-center gap-2 text-[#4C5551] py-3" data-testid="caregiver-checkin-loading">
                        <Loader2 className="animate-spin" size={16} /> Lane is thinking of you…
                    </div>
                ) : picked && content ? (
                    <div className="flex-1 min-w-0" data-testid="caregiver-checkin-response">
                        <p className="text-[11px] uppercase tracking-wider font-semibold text-[#12776F]">
                            You said: {pickedOpt?.label || picked}
                        </p>
                        <p className="mt-2 text-[#1F2624] leading-relaxed" data-testid="caregiver-response-text">
                            {content.text}
                        </p>
                        <p className="mt-3 font-medium text-[#2A3330] leading-relaxed" data-testid="caregiver-response-followup">
                            {content.follow_up}
                        </p>
                        {(content.quick_replies || []).length > 0 && (
                            <div className="mt-3 flex flex-wrap gap-2" data-testid="caregiver-quick-replies">
                                {content.quick_replies.map((chip, i) => (
                                    <button
                                        key={i}
                                        type="button"
                                        onClick={() => chipTap(chip)}
                                        disabled={sending}
                                        data-testid={`caregiver-quick-reply-${i}`}
                                        className="inline-flex items-center text-sm min-h-[40px] rounded-2xl px-4 py-1.5 border bg-white text-[#2D3748] border-[#E4E8EC] hover:border-[#12776F]/40 hover:bg-[#F4F8F9] transition-all disabled:opacity-60"
                                    >
                                        {chip}
                                    </button>
                                ))}
                            </div>
                        )}
                        <form
                            className="mt-3 flex items-center gap-2"
                            onSubmit={(e) => { e.preventDefault(); submitAnswer(freeText, "free_text"); }}
                        >
                            <input
                                ref={inputRef}
                                type="text"
                                value={freeText}
                                onChange={(e) => setFreeText(e.target.value)}
                                placeholder="Or tell Lane in your own words…"
                                data-testid="caregiver-free-text"
                                className="ml-input flex-1 text-sm !min-h-[44px]"
                            />
                            <button
                                type="submit"
                                disabled={sending || !freeText.trim()}
                                data-testid="caregiver-free-text-send"
                                aria-label="Send to Lane"
                                className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-[#12776F] text-white hover:bg-[#0F615A] disabled:opacity-40 transition-colors shrink-0"
                            >
                                {sending ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} strokeWidth={1.8} />}
                            </button>
                        </form>
                    </div>
                ) : !lastLoaded ? (
                    <div className="flex-1 min-w-0 py-1" data-testid="caregiver-checkin-skeleton" aria-hidden="true">
                        <div className="h-6 w-2/3 max-w-[320px] rounded-full bg-[#EEF2F4] animate-pulse" />
                        <div className="mt-3 h-4 w-1/2 max-w-[220px] rounded-full bg-[#F4F6F8] animate-pulse" />
                        <div className="mt-4 flex flex-wrap gap-2.5">
                            {[0, 1, 2].map((i) => (
                                <div key={i} className="h-[44px] w-28 rounded-full bg-white/70 border border-[#E7EBEE] animate-pulse" />
                            ))}
                        </div>
                    </div>
                ) : (
                    <div className="flex-1 min-w-0">
                        <h2 data-testid="caregiver-checkin-heading" className="font-heading text-xl md:text-2xl text-[#2D3748] leading-snug">
                            {heading}
                        </h2>
                        <p className="mt-1 text-sm text-[#4C5551]" data-testid="caregiver-checkin-subtext">
                            Tap one. Lane will tell you what usually helps.
                        </p>
                        <div className="mt-4 flex flex-wrap gap-2.5" data-testid="caregiver-checkin-options">
                            {CAREGIVER_OPTIONS.map((opt) => (
                                <button
                                    key={opt.code}
                                    type="button"
                                    onClick={() => tap(opt.code)}
                                    disabled={loading}
                                    data-testid={`caregiver-option-${opt.code}`}
                                    className="inline-flex items-center gap-2 text-sm font-medium min-h-[44px] rounded-2xl px-4 py-2 border bg-white hover:shadow-sm transition-all disabled:opacity-60"
                                    style={{ borderColor: `${opt.color}4D`, color: opt.color }}
                                >
                                    <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: opt.color }} aria-hidden="true" />
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
}

/* ---------------- Tier 3 — FIXED crisis pathway. Never generated. ----------------
 * FULL-SCREEN TAKEOVER (portal to body, above bottom nav, body scroll locked):
 * a caregiver in crisis must see all three resources AND all three safety
 * choices without scrolling at 390px, with zero tap-through to anything else.
 * Risk-to-SELF and risk-to-CARE-RECIPIENT keep completely separate scripts. */
function CrisisPanel({ path, flagId, onClose }) {
    const [choice, setChoice] = useState(null);
    const recipient = path === "care_recipient";

    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = prev; };
    }, []);

    const pick = (c) => {
        setChoice(c);
        if (flagId) api.post("/me/caregiver-checkin/safety-ack", { flag_id: flagId, choice: c }).catch(() => {});
    };

    return createPortal(
        <div
            className="fixed inset-0 z-[999] overflow-y-auto overscroll-contain"
            style={{ background: "linear-gradient(160deg, #FDF8EE 0%, #FBF3F0 100%)" }}
            role="dialog"
            aria-modal="true"
            aria-label="Support resources"
            data-testid="caregiver-crisis-overlay"
        >
            <div className="mx-auto w-full max-w-lg px-5 pt-5 pb-10 md:pt-16" data-testid="caregiver-crisis-panel" data-crisis-path={path}>
                <p className="text-[15px] text-[#1F2624] leading-relaxed font-medium" data-testid="crisis-fixed-text">
                    {recipient
                        ? "I'm really glad you told me. Being afraid you might hurt someone you love is one of the hardest things a caregiver can admit, and saying it out loud is the right thing to do. It usually means you're past exhausted, not that you're a bad person. I want you to be able to talk to someone tonight who understands this exact situation."
                        : "I'm really glad you told me. What you've shared tells me you may be carrying more than anyone should carry alone right now, and I want you to be able to talk to a real person tonight."}
                </p>

                <div className="mt-4 space-y-2" data-testid="crisis-resources">
                    {recipient ? (
                        <>
                            <ResourceRow href="tel:911" Icon={Phone} label="911" sub="If anyone is in immediate danger right now" testid="crisis-911" />
                            <ResourceRow href="tel:8002723900" Icon={Phone} label="Alzheimer's Association Helpline" sub="800-272-3900 · 24/7 · they handle this specific situation every day" testid="crisis-alz" />
                            <ResourceRow href="tel:988" Icon={Phone} label="988 Suicide & Crisis Lifeline" sub="Call or text 988 · if you also need support for yourself" testid="crisis-988" />
                        </>
                    ) : (
                        <>
                            <ResourceRow href="tel:988" Icon={Phone} label="988 Suicide & Crisis Lifeline" sub="Call or text 988 · free · 24/7" testid="crisis-988" />
                            <ResourceRow href="tel:8002723900" Icon={Phone} label="Alzheimer's Association Helpline" sub="800-272-3900 · 24/7 · they know caregiving" testid="crisis-alz" />
                            <ResourceRow href="tel:911" Icon={Phone} label="911" sub="If there is immediate danger" testid="crisis-911" />
                        </>
                    )}
                </div>

            {!choice ? (
                <div className="mt-5">
                    <p className="font-medium text-[#1F2624]" data-testid="crisis-safe-question">Are you safe right now?</p>
                    <div className="mt-2.5 flex flex-col sm:flex-row flex-wrap gap-2">
                        <ChoiceBtn onClick={() => pick("yes_need_talk")} testid="crisis-choice-yes">Yes, but I need someone to talk to</ChoiceBtn>
                        <ChoiceBtn onClick={() => pick("not_sure")} testid="crisis-choice-unsure">I'm not sure</ChoiceBtn>
                        <ChoiceBtn onClick={() => pick("not_safe")} testid="crisis-choice-no">No, I don't feel safe</ChoiceBtn>
                    </div>
                </div>
            ) : choice === "yes_need_talk" ? (
                <div className="mt-5 rounded-2xl bg-white/80 border border-[#EAD9C0] p-4" data-testid="crisis-path-yes">
                    <p className="text-sm text-[#1F2624] leading-relaxed">
                        Then let's get you to a real person tonight. The <strong>Alzheimer's Association Helpline
                        (800-272-3900)</strong> is staffed 24/7 by people who understand exactly what caregiving
                        asks of you — many callers say it's the first time they felt understood. If it feels
                        bigger than caregiving, <strong>988</strong> answers by call or text, day or night.
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                        <a href="tel:8002723900" className="inline-flex items-center gap-2 rounded-2xl bg-[#8A5A2B] text-white px-4 py-2.5 text-sm font-semibold" data-testid="crisis-yes-call-alz"><Phone size={14} /> Call 800-272-3900</a>
                        <a href="sms:988" className="inline-flex items-center gap-2 rounded-2xl border border-[#8A5A2B] text-[#8A5A2B] px-4 py-2.5 text-sm font-semibold bg-white" data-testid="crisis-yes-text-988"><MessageSquare size={14} /> Text 988</a>
                    </div>
                    <p className="mt-3 text-sm text-[#4C5551]">
                        Lane is also here to listen while you decide — <a href="/chat" className="underline text-[#8A5A2B]" data-testid="crisis-yes-lane">talk to Lane</a>. Lane isn't a person or a crisis service, but you don't have to sit with this silently.
                    </p>
                </div>
            ) : choice === "not_sure" ? (
                <div className="mt-5 rounded-2xl bg-white/80 border border-[#EAD9C0] p-4" data-testid="crisis-path-unsure">
                    <p className="text-sm text-[#1F2624] leading-relaxed">
                        "Not sure" is reason enough — you don't need to be certain to deserve support.
                        Please connect with <strong>988 right now, by call or text</strong>, and stay with them
                        until you feel steadier. They talk with caregivers in exactly this moment every day.
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                        <a href="tel:988" className="inline-flex items-center gap-2 rounded-2xl bg-[#8A5A2B] text-white px-4 py-2.5 text-sm font-semibold" data-testid="crisis-unsure-call-988"><Phone size={14} /> Call 988 now</a>
                        <a href="sms:988" className="inline-flex items-center gap-2 rounded-2xl border border-[#8A5A2B] text-[#8A5A2B] px-4 py-2.5 text-sm font-semibold bg-white" data-testid="crisis-unsure-text-988"><MessageSquare size={14} /> Text 988</a>
                    </div>
                </div>
            ) : (
                <div className="mt-5 rounded-2xl bg-white/90 border-2 border-red-200 p-4" data-testid="crisis-path-no">
                    <p className="text-sm text-[#1F2624] leading-relaxed font-medium">
                        Please call <strong>911 now</strong> if you or the person you care for is in immediate
                        danger. If you can, move to another room or step outside while you call.
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                        <a href="tel:911" className="inline-flex items-center gap-2 rounded-2xl bg-red-600 text-white px-5 py-2.5 text-sm font-bold" data-testid="crisis-no-call-911"><Phone size={14} /> Call 911</a>
                        <a href="tel:988" className="inline-flex items-center gap-2 rounded-2xl border border-[#8A5A2B] text-[#8A5A2B] px-4 py-2.5 text-sm font-semibold bg-white" data-testid="crisis-no-call-988"><Phone size={14} /> Call 988</a>
                    </div>
                    <p className="mt-3 text-sm text-[#4C5551]">Once you're safe, the people at 988 can stay on the line with you.</p>
                </div>
            )}

                {choice && (
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="crisis-return"
                        className="mt-6 w-full text-sm text-[#8A5A2B] underline underline-offset-2 min-h-[44px]"
                    >
                        Return to Memory Lane — these numbers stay on your home screen
                    </button>
                )}
            </div>
        </div>,
        document.body
    );
}

/* Quiet in-card note that sits behind (and after) the takeover, so the
 * numbers stay one tap away once the caregiver returns to her day. */
function CrisisInlineNote({ path }) {
    const recipient = path === "care_recipient";
    return (
        <div className="flex-1 min-w-0" data-testid="caregiver-crisis-inline">
            <p className="text-sm text-[#1F2624] leading-relaxed font-medium">
                You&apos;re not alone in this. These lines are here whenever you need them:
            </p>
            <div className="mt-3 space-y-2">
                {recipient ? (
                    <>
                        <ResourceRow href="tel:911" Icon={Phone} label="911" sub="If anyone is in immediate danger" testid="crisis-inline-911" />
                        <ResourceRow href="tel:8002723900" Icon={Phone} label="Alzheimer's Association Helpline" sub="800-272-3900 · 24/7" testid="crisis-inline-alz" />
                        <ResourceRow href="tel:988" Icon={Phone} label="988 Suicide & Crisis Lifeline" sub="Call or text 988" testid="crisis-inline-988" />
                    </>
                ) : (
                    <>
                        <ResourceRow href="tel:988" Icon={Phone} label="988 Suicide & Crisis Lifeline" sub="Call or text 988 · free · 24/7" testid="crisis-inline-988" />
                        <ResourceRow href="tel:8002723900" Icon={Phone} label="Alzheimer's Association Helpline" sub="800-272-3900 · 24/7" testid="crisis-inline-alz" />
                        <ResourceRow href="tel:911" Icon={Phone} label="911" sub="If there is immediate danger" testid="crisis-inline-911" />
                    </>
                )}
            </div>
        </div>
    );
}

function ResourceRow({ href, Icon, label, sub, testid }) {
    return (
        <a href={href} data-testid={testid}
            className="flex items-center gap-3 rounded-2xl bg-white border border-[#EAD9C0] px-4 py-3 hover:border-[#B0713A] transition-colors">
            <span className="w-9 h-9 rounded-xl bg-[#FDF3E7] flex items-center justify-center shrink-0">
                <Icon size={15} strokeWidth={2} className="text-[#8A5A2B]" />
            </span>
            <span className="min-w-0">
                <span className="block text-sm font-semibold text-[#1F2624]">{label}</span>
                <span className="block text-xs text-[#4C5551]">{sub}</span>
            </span>
        </a>
    );
}

function ChoiceBtn({ onClick, testid, children }) {
    return (
        <button type="button" onClick={onClick} data-testid={testid}
            className="text-sm min-h-[44px] rounded-2xl px-4 py-2 border bg-white text-[#2D3748] border-[#EAD9C0] hover:border-[#B0713A] hover:bg-[#FDF3E7] transition-all text-left">
            {children}
        </button>
    );
}
